import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

import { AuthSpinner } from "@/components/AuthSpinner";
import { A } from "@/lib/authStyles";
import { usePostAuthRedirect } from "@/lib/postAuthRedirect";
import { GoogleSignInButton, friendlyGoogleError } from "@/components/GoogleSignInButton";
import { events, track } from "@/lib/analytics";
import { PasswordInput } from "@/components/PasswordInput";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email").max(255),
  password: z.string().min(6, "Password must be at least 6 characters").max(72),
});

export const EMAIL_NOT_CONFIRMED = "EMAIL_NOT_CONFIRMED" as const;

const TIMEOUT = "AUTH_TIMEOUT";

/** Rejects with TIMEOUT if the promise doesn't settle in `ms`. */
function withTimeout<T>(p: PromiseLike<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error(TIMEOUT)), ms);
    Promise.resolve(p).then(
      (v) => {
        window.clearTimeout(id);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(id);
        reject(e);
      },
    );
  });
}

/**
 * Spinner that offers an escape hatch if sign-in hasn't resolved in 8s,
 * so a stalled network request can never trap the user forever.
 */
function StuckAwareSpinner({ onCancel }: { onCancel: () => void }) {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setStuck(true), 8000);
    return () => window.clearTimeout(id);
  }, []);
  if (!stuck) return <AuthSpinner label="Signing you in…" />;
  return (
    <div
      style={{
        minHeight: "100vh",
        background: A.bg,
        color: A.text,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div style={{ fontSize: 14, maxWidth: 320 }}>
        This is taking longer than usual. Your connection may have dropped.
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: "transparent",
            border: `1px solid ${A.border}`,
            color: A.text,
            padding: "10px 16px",
            fontSize: 11,
            fontFamily: "'Archivo Black', sans-serif",
            textTransform: "uppercase",
            letterSpacing: 2,
            cursor: "pointer",
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: A.text,
            border: `1px solid ${A.text}`,
            color: A.bg,
            padding: "10px 16px",
            fontSize: 11,
            fontFamily: "'Archivo Black', sans-serif",
            textTransform: "uppercase",
            letterSpacing: 2,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

const friendlyError = (msg: string): string => {
  const m = msg.toLowerCase();
  if (m.includes("invalid login")) return "That email and password don't match. Try again.";
  if (m.includes("email not confirmed") || m.includes("not confirmed"))
    return EMAIL_NOT_CONFIRMED;
  if (m.includes("network")) return "Network issue — check your connection.";
  return msg;
};

export default function Login() {
  // Note: AuthContext.onAuthStateChange handles role refresh automatically on
  // successful sign-in — no need to await refreshRole() here (saves a round-trip).
  const redirectAfterAuth = usePostAuthRedirect();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const signupSearch = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
    ? `?next=${encodeURIComponent(nextParam)}`
    : "";

  // Surface the post-reset success toast and strip the marker from the URL
  // so it doesn't fire again on refresh / back-navigation.
  useEffect(() => {
    if (searchParams.get("reset") === "success") {
      toast.success("Password reset successfully. Please log in with your new password.");
      const next = new URLSearchParams(searchParams);
      next.delete("reset");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    const t0 = performance.now();
    try {
      const { data, error: err } = await withTimeout(
        supabase.auth.signInWithPassword({
          email: parsed.data.email,
          password: parsed.data.password,
        }),
        15000,
      );
      const t1 = performance.now();
      console.log(`[timing] signInWithPassword() took ${(t1 - t0).toFixed(1)}ms`);
      if (err || !data.user) {
        setError(friendlyError(err?.message ?? "Couldn't sign in."));
        setSubmitting(false);
        return;
      }

      // Determine primary role (admin > coach > client) and redirect.
      // AuthContext.onAuthStateChange already kicks off its own role lookup in
      // parallel — we don't await refreshRole() here because that would serialize
      // an extra round-trip and noticeably delay the redirect on slow networks.
      // The lookup is time-boxed: a stalled request must never strand the user
      // on an infinite "Signing you in…" spinner.
      const t2 = performance.now();
      let primary: "admin" | "coach" | "client" = "client";
      try {
        const { data: roleRows } = await withTimeout(
          supabase.from("user_roles").select("role").eq("user_id", data.user.id),
          8000,
        );
        const roles = (roleRows ?? []).map((r) => r.role as "admin" | "coach" | "client");
        primary = roles.includes("admin")
          ? "admin"
          : roles.includes("coach")
            ? "coach"
            : roles.includes("client")
              ? "client"
              : "client";
      } catch (roleErr) {
        console.warn("[auth] role lookup failed/timed out, continuing as client", roleErr);
      }
      console.log(`[timing] Login role query took ${(performance.now() - t2).toFixed(1)}ms`);
      track(events.loginCompleted, { method: "password", role: primary });
      console.log(`[timing] Login total submit-to-redirect: ${(performance.now() - t0).toFixed(1)}ms`);
      setSubmitting(false);
      redirectAfterAuth(primary);
    } catch (e2) {
      // Timeout or unexpected throw — never leave the spinner running.
      const msg = e2 instanceof Error ? e2.message : String(e2 ?? "");
      setError(
        msg === TIMEOUT
          ? "Sign-in is taking longer than expected. Check your connection and try again."
          : friendlyError(msg || "Couldn't sign in."),
      );
      setSubmitting(false);
    }
  };

  if (submitting) return <StuckAwareSpinner onCancel={() => {
    setSubmitting(false);
    setError("Sign-in didn't complete. Please try again.");
  }} />;



  return (
    <AuthShell title="Welcome back." subtitle="Pick up where you left off.">
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
        <GoogleSignInButton onError={(msg) => setError(msg)} />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          margin: "4px 0 14px",
          color: A.sub,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 3,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        }}
      >
        <div style={{ flex: 1, height: 1, background: A.border }} />
        <span>or</span>
        <div style={{ flex: 1, height: 1, background: A.border }} />
      </div>
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Email">
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            placeholder="you@example.com"
            required
          />
        </Field>
        <Field label="Password">
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Link
              to="/forgot-password"
              style={{ ...linkStyle, fontSize: 12 }}
            >
              Forgot it?
            </Link>
          </div>
        </Field>
        {error === EMAIL_NOT_CONFIRMED ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <ErrorMessage>
              Confirm your email first. Check inbox (and spam) for the link.
            </ErrorMessage>
            <button
              type="button"
              onClick={async () => {
                if (!email) {
                  toast.error("Enter your email above first.");
                  return;
                }
                const { error: resendErr } = await supabase.auth.resend({
                  type: "signup",
                  email,
                  options: { emailRedirectTo: "https://coachaifit.com/auth" },
                });
                if (resendErr) toast.error(resendErr.message);
                else toast.success("Confirmation email re-sent.");
              }}
              style={{
                background: "transparent",
                border: `1px solid ${A.border}`,
                color: A.text,
                padding: "10px 14px",
                fontSize: 11,
                fontFamily: "'Archivo Black', sans-serif",
                textTransform: "uppercase",
                letterSpacing: 2,
                cursor: "pointer",
              }}
            >
              Resend confirmation
            </button>
          </div>
        ) : (
          error && <ErrorMessage>{error}</ErrorMessage>
        )}
        <button type="submit" style={primaryButton}>Log in</button>
      </form>

      <div
        style={{
          marginTop: 22,
          fontSize: 11,
          color: A.sub,
          textAlign: "center",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          letterSpacing: 2,
          textTransform: "uppercase",
        }}
      >
        New here?{" "}
        <Link to={`/signup-client${signupSearch}`} style={linkStyle}>Sign up</Link>
        {" · "}
        <Link to="/signup-coach" style={linkStyle}>Coach signup</Link>

      </div>
    </AuthShell>
  );
}

/* ── Shared layout primitives (kept inline so each auth page is self-contained) ── */

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: A.bg,
        color: A.text,
        fontFamily: "'Inter', system-ui, sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: A.surface,
          border: `1px solid ${A.border}`,
          padding: "40px 32px",
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontWeight: 500,
            fontSize: 11,
            color: A.teal,
            letterSpacing: 4,
            textTransform: "uppercase",
            marginBottom: 24,
          }}
        >
          // Coach AI
        </div>
        <h1
          style={{
            fontFamily: "'Archivo Black', sans-serif",
            fontSize: 36,
            fontWeight: 900,
            margin: "0 0 8px",
            color: "#ffffff",
            textTransform: "uppercase",
            letterSpacing: "-0.02em",
            lineHeight: 0.95,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: 14,
            color: A.sub,
            marginBottom: 28,
          }}
        >
          {subtitle}
        </p>
        {children}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          color: A.sub,
          textTransform: "uppercase",
          letterSpacing: 3,
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 12, color: A.sub, marginTop: 6, fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{hint}</div>
      )}
    </label>
  );
}

export function ErrorMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#f0705f10",
        border: `1px solid ${A.coral}40`,
        color: A.coral,
        padding: "12px 14px",
        fontSize: 13,
        lineHeight: 1.5,
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
      }}
    >
      {children}
    </div>
  );
}

export const inputStyle: React.CSSProperties = {
  width: "100%",
  background: A.bg,
  border: `1px solid ${A.border}`,
  color: A.text,
  padding: "14px 14px",
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  fontSize: 15,
  outline: "none",
  borderRadius: 0,
};

export const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: "pointer",
};

export const primaryButton: React.CSSProperties = {
  background: A.teal,
  color: "#050505",
  border: "none",
  padding: "16px 22px",
  fontFamily: "'Archivo Black', sans-serif",
  fontWeight: 900,
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: "0.15em",
  cursor: "pointer",
  marginTop: 8,
  borderRadius: 0,
};

export const linkStyle: React.CSSProperties = {
  color: A.teal,
  fontWeight: 700,
  textDecoration: "none",
};
